import { useState, useEffect } from "react";

interface CapacityMetricsData {
  base_gna_capacity: number;
  simulated_gna_capacity: number;
  optimized_gna_capacity: number;
  diference_in_optimized_gna_capacity: number;

  total_base_cost: number;
  total_simulated_cost: number;
  total_optimal_cost: number;
  optimal_cost_saving: number;
}

interface MetricCard {
  label: string;
  value: string;
  unit: string;
}

// Skeleton Loader Component
const SkeletonLoader = () => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
        <div
          key={item}
          className={`${
            item >= 5
              ? "bg-[#EFFCFF] border-[#34656D]"
              : "bg-[#FFFAF6] border-[#F1935C]"
          } rounded-3xl shadow-md border px-4 py-4 animate-pulse`}
        >
          <div className="space-y-2">
            {/* Label Skeleton */}
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            
            {/* Value Skeleton */}
            <div className="flex items-baseline gap-2">
              <div className="h-8 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function GnaMetricsVolumeAnalysis({
  selectedBlock,
  refreshKey,
  setError,
  loading,
  data,
}: {
  selectedBlock: any[];
  refreshKey: number;
  setError?: any;
  loading: boolean;
  data: CapacityMetricsData[];
}) {
  const [capacityData, setCapacityData] = useState<MetricCard[]>([]);

  // Format number with commas
  const formatNumber = (num: number): string => {
    return num.toLocaleString("en-IN");
  };
  const cardStyles = [
    "bg-[#ECF3FF80] border-[#617499] text-[#819ACC]", 
    "bg-[#FFFAF6] border-[#F1935C] text-[#F1935C]", 
    "bg-[#EFFCFF] border-[#34656D] text-[#34656D]",
    "bg-[#FFF6F7] border-[#FF1028] text-[#FF1028]",
  ];
  useEffect(() => {
    if (data && data.length > 0) {
      // Assuming you want to use the first item or aggregate data
      const apiData = data[0];

      const formattedData: MetricCard[] = [
        {
          label: "Base GNA Capacity",
          value: formatNumber(apiData.base_gna_capacity),
          unit: "MW",
        },
        {
          label: "Simulated GNA Capacity",
          value: formatNumber(apiData.simulated_gna_capacity),
          unit: "MW",
        },
        {
          label: "Optimal GNA Capacity",
          value: formatNumber(apiData.optimized_gna_capacity),
          unit: "MW",
        },
        {
          label: "Difference in GNA Capacity",
          value: formatNumber(apiData.diference_in_optimized_gna_capacity),
          unit: "MW",
        },
        {
          label: "Total Base Cost",
          value: formatNumber(apiData.total_base_cost),
          unit: "Lakh ₹",
        },
        {
          label: "Total Simulated Cost",
          value: formatNumber(apiData.total_simulated_cost),
          unit: "Lakh ₹",
        },
        {
          label: "Total Optimal Cost",
          value: formatNumber(apiData.total_optimal_cost),
          unit: "Lakh ₹",
        },
        {
          label: "Optimal Cost Savings",
          value: formatNumber(apiData.optimal_cost_saving),
          unit: "Lakh ₹",
        },
      ];

      setCapacityData(formattedData);
    }
  }, [data, refreshKey]);

  return (
    <>
      {loading ? (
        <div className="w-full">
          <div className="flex flex-nowrap items-center justify-around py-2 px-2">
            <div className="w-full h-full">
              <SkeletonLoader />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full">
          <div className="flex flex-nowrap items-center justify-around py-2 px-2">
            <div className="w-full h-full">
              {capacityData.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {capacityData.map((item, index) => (
                    <div
                      key={index}
                      className={`${
                        cardStyles[index % 4]
                      } rounded-3xl border px-4 py-4 transition-all hover:shadow-lg`}
                    >
                      <div className="space-y-2">
                        <h3
                          className={`text-sm font-medium ${""
                          }`}
                        >
                          {item.label}
                        </h3>
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span
                            className={`text-2xl text-[#2B3674] ${
                              index >= 4 ? "font-medium" : "font-medium"
                            }`}
                          >
                            {item.value} {item.unit}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  No Data Available...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}